import { FullApplication, OpportunityStage } from "models/FullApplication"
import { ApplicationSummaryEnhanced } from "models/ApplicationSummaryEnhanced"
import { getDevModeScenario, DevModeScenario } from "lib/Cookie"
import AdviseApplication from "./application/Advise.json"
import AdviseStatus from "./status/Advise.json"
import BookedApplication from "./application/Booked.json"
import BookedStatus from "./status/Booked.json"
import OfferApplication from "./application/Offer.json"
import OfferStatus from "./status/Offer.json"
import RecommendationPendingApplication from "./application/RecommendationPending.json"
import RecommendationPendingStatus from "./status/RecommendationPending.json"
import RecommendationReadyApplication from "./application/RecommendationReady.json"
import RecommendationReadyStatus from "./status/RecommendationReady.json"
import RecommendationAcceptedApplication from "./application/RecommendationAccepted.json"
import RecommendationAcceptedStatus from "./status/RecommendationAccepted.json"
import RecommendationAcceptedDocsRequiredApplication from "./application/RecommendationAcceptedDocsRequired.json"
import RecommendationAcceptedDocsRequiredStatus from "./status/RecommendationAcceptedDocsRequired.json"
import ApplicationApplication from "./application/Application.json"
import WonApplication from "./application/Won.json"
import CompletionSetApplication from "./application/CompletionSet.json"
import DipApplication from "./application/Dip.json"
import LostApplication from "./application/Lost.json"
import WipApplication from "./application/Wip.json"
import OfferAcceptedApplication from "./application/OfferAccepted.json"
import OfferAcceptedBookedApplication from "./application/OfferAcceptedBooked.json"
import CoreApplication from "./application/Core.json"
import ApplicationStatus from "./status/Application.json"
import WonStatus from "./status/Won.json"
import CompletionSetStatus from "./status/CompletionSet.json"
import DipStatus from "./status/Dip.json"
import LostStatus from "./status/Lost.json"
import WipStatus from "./status/Wip.json"
import OfferAcceptedStatus from "./status/OfferAccepted.json"
import OfferAcceptedBookedStatus from "./status/OfferAcceptedBooked.json"
import CoreStatus from "./status/Core.json"
import BTLSpecialistPaymentApplication from "./application/BTLSpecialistPayment.json"
import BTLSpecialistPaymentStatus from "./status/BTLSpecialistPayment.json"
import PrepareForAppointmentApplication from "./application/PrepareForAppointment.json"
import PrepareForAppointmentStatus from "./status/PrepareForAppointment.json"
import PrepareForAppointmentDocsOnlyApplication from "./application/PrepareForAppointmentDocsOnly.json"
import PrepareForAppointmentDocsOnlyStatus from "./status/PrepareForAppointmentDocsOnly.json"
// Example responses of GET /split-fact-find/{friendlyId}, built from my.mojo.api's
// src/handlers/split-fact-find/fixtures. Those files hold the records alone, so each is wrapped
// here in the `{ records, failed }` envelope the endpoint actually answers with — wrap them again
// if they are re-copied, or everything reads as "nothing completed".
import SplitFactFindNotStarted from "./splitFactFind/not-started.json"
import SplitFactFindComplete from "./splitFactFind/complete.json"
import { SplitFactFindData } from "lib/SplitFactFind"

/**
 * Summaries still come from the real api in dev mode. The prepare for appointment scenarios need a
 * Green A traffic light, an appointment still to come, and a stage that gets the advise view,
 * which a real account rarely has all at once, so they are set on the summaries here. Every other
 * scenario leaves the summaries alone.
 */
export function applySummaryScenario(
  summaries: ApplicationSummaryEnhanced[]
): ApplicationSummaryEnhanced[] {
  const scenario = getDevModeScenario()

  if (
    scenario !== DevModeScenario.PrepareForAppointment &&
    scenario !== DevModeScenario.PrepareForAppointmentDocsOnly
  ) {
    return summaries
  }

  const appointmentDate = new Date()
  appointmentDate.setDate(appointmentDate.getDate() + 7)
  appointmentDate.setHours(10, 0, 0, 0)

  return summaries.map((summary) => ({
    ...summary,
    stage: OpportunityStage.mortgageready,
    tls: { ...summary.tls, colour: "Green", tier: "A" },
    hasAppointments: true,
    appointments: [
      {
        acuityAppointmentId: 1,
        appointmentTypeId: 1,
        appointmentDate: appointmentDate.toISOString(),
        assignee: "Jane Expert",
        calendlyEventDuration: 30,
        calendlyEventName: "Mojo Mortgage Appointment",
        createdOn: new Date().toISOString(),
        isCancelled: false,
      },
    ],
  }))
}

export async function getApplication(): Promise<FullApplication> {
  const scenario = getDevModeScenario()

  if (!scenario) {
    throw new Error("Dev mode enabled but no valid scenario found")
  }

  let applicationData: any

  switch (scenario) {
    case DevModeScenario.Advise:
      applicationData = AdviseApplication
      break
    case DevModeScenario.Booked:
      applicationData = BookedApplication
      break
    case DevModeScenario.Offer:
      applicationData = OfferApplication
      break
    case DevModeScenario.RecommendationPending:
      applicationData = RecommendationPendingApplication
      break
    case DevModeScenario.RecommendationReady:
      applicationData = RecommendationReadyApplication
      break
    case DevModeScenario.RecommendationAccepted:
      applicationData = RecommendationAcceptedApplication
      break
    case DevModeScenario.RecommendationAcceptedDocsRequired:
      applicationData = RecommendationAcceptedDocsRequiredApplication
      break
    case DevModeScenario.Application:
      applicationData = ApplicationApplication
      break
    case DevModeScenario.Won:
      applicationData = WonApplication
      break
    case DevModeScenario.CompletionSet:
      applicationData = CompletionSetApplication
      break
    case DevModeScenario.Dip:
      applicationData = DipApplication
      break
    case DevModeScenario.Lost:
      applicationData = LostApplication
      break
    case DevModeScenario.Wip:
      applicationData = WipApplication
      break
    case DevModeScenario.OfferAccepted:
      applicationData = OfferAcceptedApplication
      break
    case DevModeScenario.OfferAcceptedBooked:
      applicationData = OfferAcceptedBookedApplication
      break
    case DevModeScenario.Core:
      applicationData = CoreApplication
      break
    case DevModeScenario.BTLSpecialistPayment:
      applicationData = BTLSpecialistPaymentApplication
      break
    case DevModeScenario.PrepareForAppointment:
      applicationData = PrepareForAppointmentApplication
      break
    case DevModeScenario.PrepareForAppointmentDocsOnly:
      applicationData = PrepareForAppointmentDocsOnlyApplication
      break
    default:
      throw new Error(`Unknown scenario: ${scenario}`)
  }

  console.log(`🔧 Dev mode active: Using application data from ${scenario}.json`)
  return applicationData
}

export async function getStatus(): Promise<any> {
  const scenario = getDevModeScenario()

  if (!scenario) {
    throw new Error("Dev mode enabled but no valid scenario found")
  }

  let statusData: any

  switch (scenario) {
    case DevModeScenario.Advise:
      statusData = AdviseStatus
      break
    case DevModeScenario.Booked:
      statusData = BookedStatus
      break
    case DevModeScenario.Offer:
      statusData = OfferStatus
      break
    case DevModeScenario.RecommendationPending:
      statusData = RecommendationPendingStatus
      break
    case DevModeScenario.RecommendationReady:
      statusData = RecommendationReadyStatus
      break
    case DevModeScenario.RecommendationAccepted:
      statusData = RecommendationAcceptedStatus
      break
    case DevModeScenario.RecommendationAcceptedDocsRequired:
      statusData = RecommendationAcceptedDocsRequiredStatus
      break
    case DevModeScenario.Application:
      statusData = ApplicationStatus
      break
    case DevModeScenario.Won:
      statusData = WonStatus
      break
    case DevModeScenario.CompletionSet:
      statusData = CompletionSetStatus
      break
    case DevModeScenario.Dip:
      statusData = DipStatus
      break
    case DevModeScenario.Lost:
      statusData = LostStatus
      break
    case DevModeScenario.Wip:
      statusData = WipStatus
      break
    case DevModeScenario.OfferAccepted:
      statusData = OfferAcceptedStatus
      break
    case DevModeScenario.OfferAcceptedBooked:
      statusData = OfferAcceptedBookedStatus
      break
    case DevModeScenario.Core:
      statusData = CoreStatus
      break
    case DevModeScenario.BTLSpecialistPayment:
      statusData = BTLSpecialistPaymentStatus
      break
    case DevModeScenario.PrepareForAppointment:
      statusData = PrepareForAppointmentStatus
      break
    case DevModeScenario.PrepareForAppointmentDocsOnly:
      statusData = PrepareForAppointmentDocsOnlyStatus
      break
    default:
      statusData = {
        recommendationAccepted: null,
        numberOfDocumentsRequested: 0,
        setCompletionDateForEmail: "",
        opportunityOwnerTextForEmail: "Mojo Mortgages Dev Mode",
        ownerEmailAddress: "devmode@mojomortgages.com",
      }
      break
  }

  console.log(`🔧 Dev mode active: Using status data from ${scenario}.json`)
  return statusData
}

/**
 * What Salesforce holds of the split fact find. It is only read for the prepare for appointment
 * scenarios: none of it yet, or every section, as the scenario says.
 */
export async function getSplitFactFind(): Promise<SplitFactFindData> {
  const scenario = getDevModeScenario()

  if (!scenario) {
    throw new Error("Dev mode enabled but no valid scenario found")
  }

  console.log(`🔧 Dev mode active: Using split fact find data from ${scenario}.json`)
  return scenario === DevModeScenario.PrepareForAppointmentDocsOnly
    ? SplitFactFindComplete
    : SplitFactFindNotStarted
}
